/**
 * Device React Query Hooks
 * Phase 3: 디바이스 관리 API 연동
 * API.md §6: GET /device/type/list, POST /device/register, PUT /device/{organizationDeviceId}
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
	getDeviceTypes as getDeviceTypeList,
	registerDevice,
	updateDevice,
	deleteDevice,
} from '../services/deviceService';
import type {
	GetDeviceTypeListResponseDto,
	RegisterDeviceRequestDto,
	UpdateDeviceRequestDto,
} from '../types/api/device';

export const deviceKeys = {
	all: ['device'] as const,
	types: () => [...deviceKeys.all, 'type', 'list'] as const,
};

/**
 * 디바이스 타입 리스트 조회
 * GET /device/type/list
 */
export const useDeviceTypeList = (
	options?: Omit<
		UseQueryOptions<GetDeviceTypeListResponseDto>,
		'queryKey' | 'queryFn'
	>
) => {
	return useQuery({
		queryKey: deviceKeys.types(),
		queryFn: () => getDeviceTypeList(),
		staleTime: 10 * 60 * 1000,
		...options,
	});
};

/**
 * 기기 등록
 * POST /device/register
 */
export const useRegisterDevice = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: RegisterDeviceRequestDto) => registerDevice(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: deviceKeys.all });
		},
	});
};

/**
 * 기기 정보 변경
 * PUT /device/{organizationDeviceId}
 */
export const useUpdateDevice = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			organizationDeviceId,
			data,
		}: {
			organizationDeviceId: number;
			data: UpdateDeviceRequestDto;
		}) => updateDevice(organizationDeviceId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: deviceKeys.all });
		},
	});
};

/**
 * 기기 삭제
 * DELETE /device/{organizationDeviceId} (로컬 openapi·API.md 반영 완료, BR-003)
 */
export const useDeleteDevice = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (organizationDeviceId: number) => deleteDevice(organizationDeviceId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: deviceKeys.all });
		},
	});
};
